import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface AnnouncementPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const AnnouncementPopup: React.FC<AnnouncementPopupProps> = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure smooth animation
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isVisible ? 'bg-opacity-75' : 'bg-opacity-0'
        }`}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className={`relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto transform transition-all duration-300 ${
            isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-3 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors duration-200 border border-gray-200 hover:border-red-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Content */}
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mb-4 animate-bounce">
                <span className="text-3xl">🎉</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                We were just acquired!
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto rounded-full"></div>
            </div>

            {/* Main content */}
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p className="text-lg">
                We're excited to announce that we were acquired and rebranding our 1997 brand to a fresh new name, <span className="font-semibold text-primary-600">BizBuyMax</span>.
              </p>
              
              <p>
                The new owner (<span className="font-semibold">Al Noufaro</span>) will be expanding the platform to also include Mexico, as to now cover the entire North America continent.
              </p>

              <div className="bg-gradient-to-r from-primary-50 to-secondary-50 p-6 rounded-xl border border-primary-100">
                <p className="font-semibold text-primary-800 mb-2">Our Growing Community:</p>
                <ul className="space-y-1 text-sm">
                  <li>• <span className="font-semibold">16,300+</span> active members</li>
                  <li>• <span className="font-semibold">400-600</span> new listings per month</li>
                  <li>• <span className="font-semibold">34,200+</span> email database subscribers</li>
                </ul>
              </div>

              <p className="text-center font-semibold text-primary-600">
                We're excited for the future with our new leadership.
              </p>

              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Signed, <span className="font-semibold">BizBuyMax Team!</span>
                </p>
              </div>
            </div>

            {/* Action button */}
            <div className="mt-8 text-center">
              <button
                onClick={onClose}
                className="bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Get Started with BizBuyMax
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup; 